import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Calendar as CalendarIcon,
    Clock,
    MapPin,
    Building2,
    ChevronRight,
    ChevronLeft,
    Filter,
    LayoutGrid,
    List as ListIcon,
    TrendingUp,
    X,
    CalendarCheck,
    CalendarRange,
} from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { getCompanyStory } from '../../data/companyStories';
import {
    formatDateComplete,
    formatDateCourte,
    formatJourCourt,
    formatMois,
    formatMoisAnnee,
    toDateKey,
} from '../../utils/dates';
import './CalendarScreen.css';

interface AGEvent {
    societe_nom: string;
    societe_symbole: string | null;
    date_ag: string;
    nature_ag: string | null;
    heure_ag: string | null;
    lieu: string | null;
}

interface PredictedPublication {
    societe_symbole: string;
    societe_nom: string;
    period_type: string;
    period_label: string;
    exercice_attendu: number;
    date_prevue: string;
    fiabilite: string;
    nb_points_historique: number;
    ecart_type_jours: number | null;
    taux_reussite_semaine: number | null;
    nb_cas_mesures: number;
    statut: 'PREVUE' | 'PUBLIEE';
    date_publication: string | null;
    source_url: string | null;
}

type CalendarItem =
    | {
          kind: 'AG';
          date: string;
          societe_nom: string;
          societe_symbole: string | null;
          nature_ag: string | null;
          heure_ag: string | null;
          lieu: string | null;
      }
    | {
          kind: 'PUBLICATION';
          date: string;
          societe_nom: string;
          societe_symbole: string;
          period_label: string;
          fiabilite: string;
          exercice_attendu: number;
          nb_points_historique: number;
          ecart_type_jours: number | null;
          taux_reussite_semaine: number | null;
          nb_cas_mesures: number;
          publiee: boolean;
          date_prevue: string;
          source_url: string | null;
      };

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const AG_NATURE_INFO: { [key: string]: { abbr: string; bg: string; text: string; border: string; dot: string } } = {
    Ordinaire: { abbr: 'AGO', bg: 'rgba(16, 185, 129, 0.1)', text: '#059669', border: '#a7f3d0', dot: '#10b981' },
    Extraordinaire: { abbr: 'AGE', bg: 'rgba(59, 130, 246, 0.1)', text: '#2563eb', border: '#bfdbfe', dot: '#3b82f6' },
    Mixte: { abbr: 'AGOE', bg: 'rgba(245, 158, 11, 0.1)', text: '#d97706', border: '#fcd34d', dot: '#f59e0b' },
};

const getAgNatureInfo = (nature: string | null) =>
    (nature && AG_NATURE_INFO[nature]) || {
        abbr: nature || 'AG',
        bg: 'rgba(100, 116, 139, 0.1)',
        text: '#64748b',
        border: '#cbd5e1',
        dot: '#64748b',
    };

const PUBLICATION_COLOR = {
    bg: 'rgba(99, 102, 241, 0.1)',
    text: '#4f46e5',
    border: '#c7d2fe',
    dot: '#6366f1',
};

const FIABILITE_INFO: { [key: string]: { label: string; color: string } } = {
    'ÉLEVÉE': { label: 'Fiabilité élevée', color: '#059669' },
    MOYENNE: { label: 'Fiabilité moyenne', color: '#d97706' },
    FAIBLE: { label: 'Fiabilité faible', color: '#dc2626' },
};

// Fiabilité calibrée par l'API (rejeu sur l'historique) : on montre le taux mesuré tel quel.
const precisionMesuree = (taux: number | null, nbCas: number): string =>
    taux === null || nbCas === 0
        ? 'Précision non mesurée : historique insuffisant pour ce type de publication'
        : `Parue à une semaine près ${Math.round(taux * 10)} fois sur 10, mesuré sur ${nbCas} publications de ce type`;

const CalendarScreen: React.FC = () => {
    const { token } = useAuth();
    const [items, setItems] = useState<CalendarItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'AG' | 'PUBLICATION'>('all');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() =>
        typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'grid'
    );
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    useEffect(() => {
        if (token) {
            fetchCalendarData();
        }
    }, [token]);

    const fetchCalendarData = async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${token}` };
            const [agResponse, publicationsResponse] = await Promise.all([
                fetch(`${API_URL}/v1/calendar/ag/all?limit=100`, { headers }),
                fetch(`${API_URL}/v1/calendar/publications/predicted?inclure_publiees=true`, { headers }),
            ]);

            const merged: CalendarItem[] = [];

            if (agResponse.ok) {
                const agEvents: AGEvent[] = await agResponse.json();
                agEvents.forEach((e) =>
                    merged.push({
                        kind: 'AG',
                        date: e.date_ag,
                        societe_nom: e.societe_symbole
                            ? getCompanyStory(e.societe_symbole, e.societe_nom).nomCourt
                            : e.societe_nom,
                        societe_symbole: e.societe_symbole,
                        nature_ag: e.nature_ag,
                        heure_ag: e.heure_ag,
                        lieu: e.lieu,
                    })
                );
            }

            if (publicationsResponse.ok) {
                const publications: PredictedPublication[] = await publicationsResponse.json();
                publications.forEach((p) =>
                    merged.push({
                        kind: 'PUBLICATION',
                        date: p.date_publication ?? p.date_prevue,
                        societe_nom: getCompanyStory(p.societe_symbole, p.societe_nom).nomCourt,
                        societe_symbole: p.societe_symbole,
                        period_label: p.period_label,
                        fiabilite: p.fiabilite,
                        exercice_attendu: p.exercice_attendu,
                        nb_points_historique: p.nb_points_historique,
                        ecart_type_jours: p.ecart_type_jours,
                        taux_reussite_semaine: p.taux_reussite_semaine,
                        nb_cas_mesures: p.nb_cas_mesures,
                        publiee: p.statut === 'PUBLIEE',
                        date_prevue: p.date_prevue,
                        source_url: p.source_url,
                    })
                );
            }

            setItems(merged);
        } catch (error) {
            console.error('Error fetching calendar data:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(
        () => (filter === 'all' ? items : items.filter((i) => i.kind === filter)),
        [items, filter]
    );

    const groupItemsByMonth = () => {
        const grouped: { [key: string]: CalendarItem[] } = {};
        filteredItems.forEach((item) => {
            const monthKey = formatMoisAnnee(item.date);
            if (!grouped[monthKey]) {
                grouped[monthKey] = [];
            }
            grouped[monthKey].push(item);
        });
        return grouped;
    };

    const itemsByDate = useMemo(() => {
        const map: { [key: string]: CalendarItem[] } = {};
        filteredItems.forEach((item) => {
            const key = toDateKey(new Date(item.date));
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [filteredItems]);

    const monthMatrix = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // 0 = Lundi
        const gridStart = new Date(year, month, 1 - firstWeekday);
        const days: Date[] = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(gridStart);
            d.setDate(gridStart.getDate() + i);
            days.push(d);
        }
        return days;
    }, [currentMonth]);

    const goToPrevMonth = () => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
        setSelectedDate(null);
    };

    const goToNextMonth = () => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
        setSelectedDate(null);
    };

    const goToToday = () => {
        setCurrentMonth(new Date());
        setSelectedDate(null);
    };

    const isUpcoming = (dateStr: string) => {
        const d = new Date(dateStr);
        d.setHours(23, 59, 59, 999);
        return d >= new Date();
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return {
            day: date.getDate(),
            weekday: formatJourCourt(date),
            full: formatDateComplete(date),
        };
    };

    const getItemLabel = (item: CalendarItem) =>
        item.kind === 'AG'
            ? `AG ${item.societe_nom}`
            : `${item.period_label} ${item.societe_nom}${item.publiee ? ' (publiée)' : ''}`;

    const getItemColors = (item: CalendarItem) =>
        item.kind === 'AG' ? getAgNatureInfo(item.nature_ag) : PUBLICATION_COLOR;

    // Counts & Smart KPIs
    const upcomingCount = useMemo(() => items.filter((i) => isUpcoming(i.date)).length, [items]);
    const agCount = useMemo(() => items.filter((i) => i.kind === 'AG').length, [items]);
    const pubCount = useMemo(
        () => items.filter((i) => i.kind === 'PUBLICATION' && !i.publiee).length,
        [items]
    );

    const next30DaysCount = useMemo(() => {
        const now = new Date();
        const in30Days = new Date();
        in30Days.setDate(now.getDate() + 30);
        in30Days.setHours(23, 59, 59, 999);
        return items.filter((i) => {
            const d = new Date(i.date);
            return d >= now && d <= in30Days;
        }).length;
    }, [items]);

    const nextKeyDateEvent = useMemo(() => {
        const now = new Date();
        const upcoming = items
            .filter((i) => new Date(i.date) >= now)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        return upcoming[0] || null;
    }, [items]);

    const renderEventCard = (item: CalendarItem, idx: number) => {
        const dateInfo = formatDate(item.date);
        const upcoming = isUpcoming(item.date);
        const colors = getItemColors(item);

        return (
            <div
                key={`${item.kind}-${item.societe_nom}-${item.date}-${idx}`}
                className={`cal-event-card ${item.kind === 'PUBLICATION' ? 'is-publication' : ''} ${upcoming ? 'upcoming' : 'past'}`}
            >
                {/* Date Badge */}
                <div className={`cal-date-badge ${upcoming ? 'upcoming' : 'past'}`}>
                    <span className="cal-date-badge-weekday">{dateInfo.weekday}</span>
                    <span className="cal-date-badge-day">{dateInfo.day}</span>
                </div>

                {/* Event Content */}
                <div className="cal-event-content">
                    <div className="cal-event-header-line">
                        <div className="cal-event-icon-tag">
                            {item.kind === 'AG' ? (
                                <Building2 size={18} color="#059669" />
                            ) : (
                                <TrendingUp size={18} color="#4f46e5" />
                            )}
                        </div>

                        <span className="cal-event-company-name">{item.societe_nom}</span>

                        {item.societe_symbole && (
                            <Link
                                to={`/data?symbol=${item.societe_symbole}`}
                                className="cal-stock-symbol-tag"
                                title={`Voir la fiche ${item.societe_symbole} sur le Terminal`}
                            >
                                {item.societe_symbole}
                            </Link>
                        )}

                        {item.kind === 'AG' ? (
                            <span
                                className="cal-badge-nature"
                                style={{
                                    background: colors.bg,
                                    color: colors.text,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                {getAgNatureInfo(item.nature_ag).abbr}
                            </span>
                        ) : (
                            <span
                                className="cal-badge-nature"
                                style={{
                                    background: PUBLICATION_COLOR.bg,
                                    color: PUBLICATION_COLOR.text,
                                    border: `1px solid ${PUBLICATION_COLOR.border}`,
                                }}
                            >
                                {item.period_label} · {item.exercice_attendu}
                            </span>
                        )}
                    </div>

                    {item.kind === 'AG' ? (
                        <div className="cal-event-meta-line">
                            {item.heure_ag && (
                                <span className="cal-meta-item">
                                    <Clock size={14} /> {item.heure_ag}
                                </span>
                            )}
                            {item.lieu && (
                                <span className="cal-meta-item">
                                    <MapPin size={14} /> {item.lieu}
                                </span>
                            )}
                        </div>
                    ) : item.publiee ? (
                        <div className="cal-fiabilite-text">
                            {`Publiée le ${formatDateCourte(item.date)} — prévue le ${formatDateCourte(item.date_prevue)}`}
                            {item.source_url && (
                                <>
                                    {' · '}
                                    <a href={item.source_url} target="_blank" rel="noopener noreferrer">
                                        Voir le rapport
                                    </a>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="cal-fiabilite-text">
                            <span
                                className="cal-fiabilite-chip"
                                title={precisionMesuree(item.taux_reussite_semaine, item.nb_cas_mesures)}
                                style={{ color: FIABILITE_INFO[item.fiabilite]?.color || 'var(--text-muted)' }}
                            >
                                <span
                                    style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        background: FIABILITE_INFO[item.fiabilite]?.color || 'currentColor',
                                    }}
                                />
                                {FIABILITE_INFO[item.fiabilite]?.label || item.fiabilite}
                            </span>
                            {' — Date prévisionnelle'}
                        </div>
                    )}
                </div>

                {/* Arrow CTA */}
                <div className="cal-arrow">
                    <ChevronRight size={18} />
                </div>
            </div>
        );
    };

    const groupedItems = groupItemsByMonth();
    const todayKey = toDateKey(new Date());

    return (
        <SiteLayout>
            <Header icon="event" title="Calendrier" />

            <div className="cal-container">
                {/* ── Page Header & Quick CTA ── */}
                <div className="cal-header-row">
                    <div className="cal-header-titles">
                        <h1 className="cal-page-title">Calendrier Financier BRVM</h1>
                        <p className="cal-page-subtitle">
                            Assemblées Générales et estimations prévisionnelles des publications de résultats.
                        </p>
                    </div>
                    <Link to="/resultats" className="cal-results-link">
                        <span>Résultats déjà publiés</span>
                        <ChevronRight size={15} />
                    </Link>
                </div>

                {/* ── KPI Summary Cards (Desktop focus) ── */}
                <div className="cal-summary-grid">
                    {/* Card 1: 30-Day Upcoming Horizon */}
                    <div className="cal-kpi-card gradient">
                        <div className="cal-kpi-header">
                            <div className="cal-kpi-header-left">
                                <div className="cal-kpi-icon-wrap purple">
                                    <Clock size={20} />
                                </div>
                                <span className="cal-kpi-title">Échéances à 30 jours</span>
                            </div>
                            <span className="cal-kpi-badge">Court terme</span>
                        </div>
                        <div className="cal-kpi-num">{next30DaysCount}</div>
                        <div className="cal-kpi-sub">
                            {upcomingCount} événements au total sur l'exercice
                        </div>
                    </div>

                    {/* Card 2: Prochaine date clé */}
                    <div className="cal-kpi-card standard">
                        <div className="cal-kpi-header">
                            <div className="cal-kpi-header-left">
                                <div className="cal-kpi-icon-wrap emerald">
                                    <CalendarCheck size={20} />
                                </div>
                                <span className="cal-kpi-title">Prochain rendez-vous</span>
                            </div>
                            {nextKeyDateEvent && (
                                <span className="cal-kpi-sub-badge">
                                    {nextKeyDateEvent.kind === 'AG' ? 'AG' : 'Résultats'}
                                </span>
                            )}
                        </div>
                        {nextKeyDateEvent ? (
                            <>
                                <div className="cal-kpi-company-highlight">
                                    {nextKeyDateEvent.societe_nom}
                                </div>
                                <div className="cal-kpi-sub">
                                    {formatDateComplete(new Date(nextKeyDateEvent.date))}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="cal-kpi-num">—</div>
                                <div className="cal-kpi-sub">Aucune échéance immédiate</div>
                            </>
                        )}
                    </div>

                    {/* Card 3: Publications de résultats attendues */}
                    <div className="cal-kpi-card standard">
                        <div className="cal-kpi-header">
                            <div className="cal-kpi-header-left">
                                <div className="cal-kpi-icon-wrap indigo">
                                    <TrendingUp size={20} />
                                </div>
                                <span className="cal-kpi-title">Publications prévues</span>
                            </div>
                            <span
                                className="cal-kpi-sub-badge estimate"
                                title="Délai de publication médian des 3 derniers exercices, affiché seulement si la société a tenu un calendrier régulier"
                            >
                                Estimation statistique
                            </span>
                        </div>
                        <div className="cal-kpi-num">{pubCount}</div>
                        <div className="cal-kpi-sub">
                            <span>Anticipations des résultats financiers</span>
                        </div>
                    </div>
                </div>

                {/* ── Toolbar: Filters & View Switcher ── */}
                <div className="cal-toolbar">
                    <div className="cal-filter-group">
                        <span className="cal-filter-label">
                            <Filter size={15} />
                            <span>Filtrer :</span>
                        </span>

                        <button
                            type="button"
                            className={`cal-filter-btn ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            <span>Tous</span>
                            <span className="cal-filter-count">{items.length}</span>
                        </button>

                        <button
                            type="button"
                            className={`cal-filter-btn ${filter === 'AG' ? 'active' : ''}`}
                            onClick={() => setFilter('AG')}
                        >
                            <span>AG</span>
                            <span className="cal-filter-count">{agCount}</span>
                        </button>

                        <button
                            type="button"
                            className={`cal-filter-btn ${filter === 'PUBLICATION' ? 'active' : ''}`}
                            onClick={() => setFilter('PUBLICATION')}
                        >
                            <span>Publication</span>
                            <span className="cal-filter-count">{pubCount}</span>
                        </button>
                    </div>

                    {/* View Switcher: Grid vs List */}
                    <div className="cal-view-toggle">
                        <button
                            type="button"
                            className={`cal-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            aria-label="Vue calendrier"
                        >
                            <LayoutGrid size={15} />
                            <span className="cal-view-toggle-label">Calendrier</span>
                        </button>

                        <button
                            type="button"
                            className={`cal-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            aria-label="Vue liste"
                        >
                            <ListIcon size={15} />
                            <span className="cal-view-toggle-label">Liste</span>
                        </button>
                    </div>
                </div>

                {/* ── Main Content Area ── */}
                {loading ? (
                    <div className="cal-state-box">
                        <div className="cal-loader-spinner" />
                        <div>Chargement du calendrier...</div>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div>
                        {/* Month Navigation */}
                        <div className="cal-month-nav">
                            <button
                                type="button"
                                className="cal-nav-arrow-btn"
                                onClick={goToPrevMonth}
                                aria-label="Mois précédent"
                            >
                                <ChevronLeft size={18} />
                            </button>

                            <div className="cal-month-title-wrap">
                                <span className="cal-month-title">{formatMois(currentMonth)}</span>
                                <button type="button" className="cal-today-btn" onClick={goToToday}>
                                    <CalendarCheck size={14} />
                                    <span>Aujourd'hui</span>
                                </button>
                            </div>

                            <button
                                type="button"
                                className="cal-nav-arrow-btn"
                                onClick={goToNextMonth}
                                aria-label="Mois suivant"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>

                        {/* Weekday Row */}
                        <div className="cal-grid-weekdays">
                            {WEEKDAY_LABELS.map((wd, i) => (
                                <div
                                    key={wd}
                                    className={`cal-weekday-cell ${i >= 5 ? 'weekend' : ''}`}
                                >
                                    {wd}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid 7x6 */}
                        <div className="cal-grid">
                            {monthMatrix.map((day) => {
                                const key = toDateKey(day);
                                const dayItems = itemsByDate[key] || [];
                                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                                const isToday = key === todayKey;
                                const isSelected = key === selectedDate;
                                const hasItems = dayItems.length > 0;
                                const visibleChips = dayItems.slice(0, 3);
                                const extraCount = dayItems.length - visibleChips.length;

                                return (
                                    <div
                                        key={key}
                                        className={`cal-grid-cell ${hasItems ? 'has-events' : ''} ${
                                            isCurrentMonth ? '' : 'is-other-month'
                                        } ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                                        onClick={() => hasItems && setSelectedDate(isSelected ? null : key)}
                                    >
                                        <div className="cal-day-num">{day.getDate()}</div>

                                        {hasItems && (
                                            <div className="cal-chips-stack">
                                                {visibleChips.map((item, i) => {
                                                    const colors = getItemColors(item);
                                                    return (
                                                        <div
                                                            key={i}
                                                            className="cal-grid-chip"
                                                            style={{
                                                                background: colors.bg,
                                                                color: colors.text,
                                                                border:
                                                                    item.kind === 'PUBLICATION' && !item.publiee
                                                                        ? `1px dashed ${colors.border}`
                                                                        : `1px solid ${colors.border}`,
                                                            }}
                                                            title={getItemLabel(item)}
                                                        >
                                                            <span
                                                                className="cal-chip-dot"
                                                                style={{ background: colors.dot }}
                                                            />
                                                            <span
                                                                className="cal-chip-text"
                                                                style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                            >
                                                                {getItemLabel(item)}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                {extraCount > 0 && (
                                                    <div className="cal-grid-chip-more">+{extraCount}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Selected Date Spotlight Panel */}
                        {selectedDate && (
                            <div className="cal-selected-panel">
                                <div className="cal-selected-header">
                                    <div className="cal-selected-title-group">
                                        <CalendarIcon size={18} color="#4f46e5" />
                                        <span className="cal-selected-title">
                                            {formatDateComplete(new Date(selectedDate))}
                                        </span>
                                        <span className="cal-selected-count-badge">
                                            {(itemsByDate[selectedDate] || []).length} événement
                                            {(itemsByDate[selectedDate] || []).length > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="cal-selected-close-btn"
                                        onClick={() => setSelectedDate(null)}
                                    >
                                        <X size={14} />
                                        <span>Fermer</span>
                                    </button>
                                </div>
                                <div className="cal-selected-events-list">
                                    {(itemsByDate[selectedDate] || []).map((item, idx) =>
                                        renderEventCard(item, idx)
                                    )}
                                </div>
                            </div>
                        )}

                        {!loading && items.length === 0 && (
                            <div className="cal-state-box">
                                <CalendarIcon size={44} className="cal-empty-icon" />
                                <div className="cal-empty-title">Aucun événement trouvé</div>
                                <div className="cal-empty-sub">
                                    Aucune Assemblée Générale ni publication prévue n'a été trouvée pour ce filtre.
                                </div>
                            </div>
                        )}
                    </div>
                ) : Object.keys(groupedItems).length === 0 ? (
                    <div className="cal-state-box">
                        <CalendarIcon size={44} className="cal-empty-icon" />
                        <div className="cal-empty-title">Aucun événement trouvé</div>
                        <div className="cal-empty-sub">
                            Aucune Assemblée Générale ni publication prévue n'a été trouvée pour ce filtre.
                        </div>
                    </div>
                ) : (
                    <div className="cal-list-view">
                        {Object.entries(groupedItems).map(([month, monthItems]) => (
                            <div key={month} className="cal-list-group">
                                <div className="cal-list-group-header">
                                    <CalendarIcon size={18} color="#4f46e5" />
                                    <span>{month}</span>
                                    <span className="cal-list-group-badge">
                                        {monthItems.length} événement{monthItems.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="cal-list-events-stack">
                                    {monthItems.map((item, idx) => renderEventCard(item, idx))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </SiteLayout>
    );
};

export default CalendarScreen;
